import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import '../styles/AddProject.css';
import '../../ProfilePages/styles/QuotationForm.css';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import {
  fetchCatalogServices,
  pickFromCatalog,
  picksToServiceLines,
  saveBookingServicesForPicks,
} from '../../../utils/catalogPicks';
import CatalogServicePicks from '../../../components/CatalogServicePicks';
import {
  PROJECT_FREQUENCY_OPTIONS,
  fetchQuoteProjectFrequency,
  matchProjectFrequencyOption,
  isRecurringFrequencyOption,
} from '../../../utils/projectFrequency';
import { htmlToPlainText } from '../utils/projectUtils';

const INITIAL_FORM = {
  projectTitle: '',
  bookingId: '',
  startDate: '',
  endDate: '',
  deadlineOngoing: false,
  startTime: '',
  endTime: '',
  reminderDate: '',
  reminderTime: '',
  status: 'Draft',
  description: '',
  manager: '',
  purchaseOrderNumber: '',
  projectAddress: '',
  deliveryAddress: '',
  attention: '',
  frequency: 'One-time',
  billingCadence: 'per_visit',
  autoSendMonthlyInvoice: false,
  invoiceLineMode: 'service',
};

const FIND_BOOKING_SERVICE = `
  query FindBookingService($bookingId: String!) {
    findBookingService(bookingId: $bookingId) {
      data {
        id
        price
        total
        tier
        service {
          id
          service
        }
      }
    }
  }
`;

const FIND_CONTRACT_FOR_PROJECT = `
  query FindContractForProject($id: String!) {
    findOneClientContract(id: $id) {
      id
      contractNumber
      quotationNumber
      quotationVersionId
      bookingId
      title
      clientName
      clientEmail
      siteAddress
      contactPerson
      serviceType
      frequency
      scopeOfWork
      startDate
      endDate
      value
      status
    }
  }
`;

/** Client-portal preferred dates are free text; keep only valid YYYY-MM-DD. */
function toDateInput(value) {
  const raw = String(value || '').trim();
  if (/^d{4}-d{2}-d{2}/.test(raw)) return raw.slice(0, 10);
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
}

export default function AddProject({
  onClose,
  onCreated,
  sourceContractId = '',
  initialBookingId = '',
  initialClient = null,
  /** Client-portal "Book a service" request: start with its chosen service(s). */
  preselectRequestedServices = false,
}) {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [bookings, setBookings] = useState([]);
  const [managers, setManagers] = useState([]);
  const [services, setServices] = useState([]);
  /** Catalog services picked for this project (see utils/catalogPicks). */
  const [picks, setPicks] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [loadingServices, setLoadingServices] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sourceContract, setSourceContract] = useState(null);

  useEffect(() => {
    if (!sourceContractId) return;
    graphqlRequest(FIND_CONTRACT_FOR_PROJECT, { id: sourceContractId })
      .then((data) => setSourceContract(data?.findOneClientContract || null))
      .catch((err) => {
        console.error('Error loading source contract:', err);
        toast.error(err?.message || 'Failed to load contract details');
      });
  }, [sourceContractId]);

  useEffect(() => {
    if (!sourceContract) return;
    const contractFreq = matchProjectFrequencyOption(sourceContract.frequency);
    setFormData((prev) => ({
      ...prev,
      projectTitle: sourceContract.title || prev.projectTitle,
      startDate: sourceContract.startDate?.slice(0, 10) || prev.startDate,
      endDate: sourceContract.endDate?.slice(0, 10) || prev.endDate,
      description:
        htmlToPlainText(sourceContract.scopeOfWork) || prev.description,
      projectAddress: sourceContract.siteAddress || prev.projectAddress,
      attention: sourceContract.contactPerson || prev.attention,
      frequency: contractFreq === 'One-time' ? 'Weekly' : contractFreq,
      billingCadence: 'monthly',
      autoSendMonthlyInvoice: false,
      invoiceLineMode: 'service',
    }));
  }, [sourceContract]);

  useEffect(() => {
    if (!sourceContract || loadingClients) return;
    const bookingId = String(sourceContract.bookingId || '').trim();
    if (!bookingId) return;
    const booking = bookings.find((row) => String(row.id) === bookingId);
    const resolvedId = booking?.id || bookingId;
    setFormData((prev) => ({ ...prev, bookingId: resolvedId }));
    loadBookingServices(resolvedId);
    fetchQuoteProjectFrequency(graphqlRequest, resolvedId).then((freq) => {
      if (freq === 'One-time') return;
      setFormData((prev) =>
        prev.bookingId === resolvedId ? { ...prev, frequency: freq } : prev,
      );
    });
  // loadBookingServices is stable for the lifetime of this modal.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings, loadingClients, sourceContract]);

  useEffect(() => {
    if (sourceContract || loadingClients) return;
    const bookingId = String(initialBookingId || initialClient?.id || '').trim();
    if (!bookingId) return;
    setFormData((prev) => {
      if (prev.bookingId === bookingId) return prev;
      return {
        ...prev,
        bookingId,
        attention: prev.attention || initialClient?.fullName || '',
        startDate:
          prev.startDate ||
          (preselectRequestedServices
            ? toDateInput(initialClient?.preferredDate)
            : ''),
        projectAddress:
          prev.projectAddress ||
          [initialClient?.address, initialClient?.postcode]
            .filter(Boolean)
            .join(', ') ||
          '',
      };
    });
    loadBookingServices(bookingId);
    fetchQuoteProjectFrequency(graphqlRequest, bookingId).then((freq) => {
      if (!freq || freq === 'One-time') return;
      setFormData((prev) =>
        prev.bookingId === bookingId ? { ...prev, frequency: freq } : prev,
      );
    });
    // loadBookingServices is stable for the lifetime of this modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bookings,
    loadingClients,
    sourceContract,
    initialBookingId,
    initialClient,
    preselectRequestedServices,
  ]);

  useEffect(() => {
    if (!sourceContract || loadingServices) return;
    const wanted = String(sourceContract.serviceType || '').trim().toLowerCase();
    if (!wanted) return;
    const match = services.find(
      (row) => String(row.service || '').trim().toLowerCase() === wanted,
    );
    if (!match) return;
    setPicks([pickFromCatalog(match, '', Number(sourceContract.value) || 0)]);
  }, [loadingServices, services, sourceContract]);

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        setLoadingClients(true);
        const query = `
          query FindAllBooking($page: Float!, $limit: Float!) {
            findAllBooking(page: $page, limit: $limit, includeSignUrls: false) {
              data {
                id
                fullName
                email
                phone
                estimatedPRice
                status
                bookingFrequency
                stage {
                  id
                  name
                }
              }
            }
          }
        `;
        const data = await graphqlRequest(query, { page: 1, limit: 500 });
        setBookings(data?.findAllBooking?.data || []);
      } catch (err) {
        console.error('Error fetching bookings:', err);
        toast.error(err?.message || 'Failed to load clients');
        setBookings([]);
      } finally {
        setLoadingClients(false);
      }
    };

    fetchBookings();
  }, []);

  useEffect(() => {
    const fetchManagers = async () => {
      try {
        const query = `
          query GetAllUser($page: Int, $limit: Int, $roles: [String!]) {
            getAllUser(page: $page, limit: $limit, roles: $roles) {
              data {
                id
                firstName
                lastName
                email
              }
            }
          }
        `;
        const data = await graphqlRequest(query, {
          page: 1,
          limit: 100,
          roles: ['OPERATIONS_MANAGER', 'SUPERVISOR', 'SUPER_ADMIN', 'ADMIN', 'BRANCH_ADMIN'],
        });
        setManagers(data?.getAllUser?.data || []);
      } catch (err) {
        console.error('Error fetching managers:', err);
      }
    };

    fetchManagers();
  }, []);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoadingServices(true);
        setServices(await fetchCatalogServices());
      } catch (err) {
        console.error('Error fetching services:', err);
        toast.error(err?.message || 'Failed to load services');
        setServices([]);
      } finally {
        setLoadingServices(false);
      }
    };

    fetchServices();
  }, []);

  const loadBookingServices = async (bookingId) => {
    // Manual projects start blank. Contract-derived projects keep the service
    // from the contract; portal service bookings start with what the client chose.
    // Catalog picks don't depend on the client, so picking a client keeps them.
    if (!bookingId || !preselectRequestedServices) return;
    try {
      const [data, catalog] = await Promise.all([
        graphqlRequest(FIND_BOOKING_SERVICE, { bookingId }),
        fetchCatalogServices(),
      ]);
      const byService = new Map();
      (data?.findBookingService?.data || []).forEach((row) => {
        const svc = catalog.find((s) => s.id === row?.service?.id);
        if (!svc || byService.has(svc.id)) return;
        byService.set(
          svc.id,
          pickFromCatalog(svc, row.tier || '', Number(row.total ?? row.price) || 0),
        );
      });
      setPicks([...byService.values()]);
    } catch (err) {
      console.error('Error fetching booking services:', err);
      setPicks([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'bookingId') {
      loadBookingServices(value);
      if (!value) {
        setFormData((prev) => ({ ...prev, frequency: 'One-time' }));
        return;
      }
      fetchQuoteProjectFrequency(graphqlRequest, value).then((freq) => {
        setFormData((prev) =>
          prev.bookingId === value ? { ...prev, frequency: freq } : prev,
        );
      });
    }
  };

  const selectedTotal = picks.reduce(
    (sum, pick) => sum + (Number(pick.unitPrice) || 0),
    0,
  );

  const isPhoneConfirmedLead = (booking) => {
    if (!booking) return false;
    const status = String(booking.status || '').toLowerCase();
    if (status === 'approve' || status === 'approved') return true;
    const freq = String(booking.bookingFrequency || '');
    const label = freq.includes('|') ? freq.split('|').slice(1).join('|') : freq;
    const hay = `${booking.stage?.name || ''} ${label}`.toLowerCase();
    return /closed\s*won|\bqualified\b/.test(hay);
  };

  const selectedBooking = bookings.find((b) => b.id === formData.bookingId);
  const phonePath = isPhoneConfirmedLead(selectedBooking);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!formData.bookingId) {
      toast.error('Please select a client');
      return;
    }

    if (!formData.projectTitle.trim()) {
      toast.error('Please enter a project title');
      return;
    }

    if (!picks.length) {
      toast.error('Add at least one service');
      return;
    }

    try {
      setSubmitting(true);

      // Save selected services first so Path B draft invoice can seed from them.
      await saveBookingServicesForPicks(formData.bookingId, picks).catch((err) => {
        console.error('Error saving booking services:', err);
        throw err;
      });

      if (selectedTotal > 0 || formData.description.trim()) {
        const updateBookingMutation = `
          mutation UpdateBooking($input: UpdateBookingInput!) {
            updateBooking(updateBookingInput: $input) {
              id
              estimatedPRice
              message
            }
          }
        `;
        await graphqlRequest(updateBookingMutation, {
          input: {
            id: formData.bookingId,
            ...(selectedTotal > 0
              ? { estimatedPRice: String(selectedTotal) }
              : {}),
            ...(formData.description.trim()
              ? { message: htmlToPlainText(formData.description) }
              : {}),
          },
        }).catch((err) => console.error('Error updating booking:', err));
      }

      const mutation = `
        mutation CreateProject($input: CreateProjectInput!) {
          createProject(createProjectInput: $input) {
            id
            name
            status
            booking {
              id
            }
          }
        }
      `;

      const createRes = await graphqlRequest(mutation, {
        input: {
          bookingId: formData.bookingId,
          projectName: formData.projectTitle.trim(),
          startDate: formData.startDate || null,
          deadLine: formData.deadlineOngoing ? null : formData.endDate || null,
          deadlineOngoing: Boolean(formData.deadlineOngoing),
          startTime: formData.startTime || null,
          endTime: formData.endTime || null,
          manager: formData.manager || null,
          reuseExistingBooking: true,
          serviceIds: picks.map((pick) => pick.catalogId),
          serviceLines: picksToServiceLines(picks, formData.frequency),
          frequency: formData.frequency || 'One-time',
          billingCadence: isRecurringFrequencyOption(formData.frequency)
            ? formData.billingCadence || 'per_visit'
            : 'per_visit',
          autoSendMonthlyInvoice:
            isRecurringFrequencyOption(formData.frequency) &&
            formData.billingCadence === 'monthly'
              ? Boolean(formData.autoSendMonthlyInvoice)
              : false,
          invoiceLineMode:
            formData.billingCadence === 'monthly'
              ? formData.invoiceLineMode || 'service'
              : 'service',
          purchaseOrderNumber: formData.purchaseOrderNumber.trim() || null,
          projectAddress: formData.projectAddress.trim() || null,
          deliveryAddress: formData.deliveryAddress.trim() || null,
          attention: formData.attention.trim() || null,
          sourceContractId: sourceContractId || null,
          sourceQuotationVersionId: sourceContract?.quotationVersionId || null,
        },
      });

      const bookingId =
        createRes?.createProject?.booking?.id || formData.bookingId;
      if (!createRes?.createProject?.id || !bookingId) {
        throw new Error(
          'Project was created but could not be linked to the client.',
        );
      }

      const projectStatus = String(
        createRes?.createProject?.status || '',
      ).toLowerCase();
      if (projectStatus === 'active') {
        const monthly = formData.billingCadence === 'monthly';
        toast.success(
          monthly
            ? 'Project created as Active. Frequency is stored on this project; the invoice is drafted at month end.'
            : 'Project created as Active. Create its invoice from the project → Invoices tab when you are ready.',
        );
      } else {
        toast.success(
          'Project created as Pending. It becomes Active when the opportunity is won (accepted quote, signed contract, or Closed Won).',
        );
      }
      onCreated?.();
      onClose();
    } catch (err) {
      console.error('Error creating project:', err);
      toast.error(err?.message || 'Failed to create project');
    } finally {
      setSubmitting(false);
    }
  };

  const modal = (
    <div className="overlay-project" onClick={onClose} role="presentation">
      <div
        className="project-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-project-title"
      >
        <div className="modal-header-project">
          <div>
            <h2 id="add-project-title">Add Project</h2>
            {sourceContract ? (
              <p className="form-hint">
                Prefilled from contract {sourceContract.contractNumber}
              </p>
            ) : initialClient?.fullName || initialBookingId ? (
              <p className="form-hint">
                Prefilled from {initialClient?.fullName || 'this customer'}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="close-btn-project"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        <form className="project-form" onSubmit={handleSubmit}>
          <div className="project-form-body">
            <div className="form-group-project">
              <label htmlFor="projectTitle">Project Title</label>
              <input
                id="projectTitle"
                type="text"
                name="projectTitle"
                value={formData.projectTitle}
                onChange={handleChange}
                placeholder="Enter project title"
                required
              />
            </div>

            <div className="form-group-project">
              <label htmlFor="bookingId">Client</label>
              <select
                id="bookingId"
                name="bookingId"
                value={formData.bookingId}
                onChange={handleChange}
                required
                disabled={
                  loadingClients ||
                  Boolean(
                    initialBookingId ||
                      initialClient?.id ||
                      sourceContract?.bookingId,
                  )
                }
              >
                <option value="">
                  {loadingClients ? 'Loading clients...' : 'Select client'}
                </option>
                {sourceContract?.bookingId &&
                !bookings.some(
                  (row) => String(row.id) === String(sourceContract.bookingId),
                ) ? (
                  <option value={sourceContract.bookingId}>
                    {sourceContract.clientName || 'Contract client'}
                    {sourceContract.clientEmail
                      ? ` — ${sourceContract.clientEmail}`
                      : ''}
                  </option>
                ) : null}
                {initialClient?.id &&
                !bookings.some(
                  (row) => String(row.id) === String(initialClient.id),
                ) ? (
                  <option value={initialClient.id}>
                    {initialClient.fullName}
                    {initialClient.email ? ` — ${initialClient.email}` : ''}
                  </option>
                ) : null}
                {bookings.map((booking) => (
                  <option key={booking.id} value={booking.id}>
                    {booking.fullName}
                    {booking.email ? ` — ${booking.email}` : ''}
                  </option>
                ))}
              </select>
              <p className="form-hint">
                {phonePath
                  ? 'This lead is already won — this project will be Active. Create its invoice when you are ready.'
                  : 'Project will be Pending. It becomes Active when the opportunity is won (accepted quote, signed contract, or Closed Won).'}
              </p>
              {sourceContract && !sourceContract.bookingId ? (
                <p className="form-hint">
                  This contract has no linked client id. Open the lead and
                  attach it on the contract before creating a project.
                </p>
              ) : null}
            </div>

            <div className="form-group-project">
              <label htmlFor="attention">Attention (optional)</label>
              <input
                id="attention"
                type="text"
                name="attention"
                value={formData.attention}
                onChange={handleChange}
                placeholder="Client contact attending the site"
              />
              <p className="form-hint">
                Who from the client side is attending / should be asked for on
                site.
              </p>
            </div>

            <div className="form-group-project">
              <label htmlFor="purchaseOrderNumber">
                Purchase Order Number (optional)
              </label>
              <input
                id="purchaseOrderNumber"
                type="text"
                name="purchaseOrderNumber"
                value={formData.purchaseOrderNumber}
                onChange={handleChange}
                placeholder="PO number for invoice"
              />
            </div>

            <div className="form-group-project">
              <label htmlFor="projectAddress">Project Address (optional)</label>
              <input
                id="projectAddress"
                type="text"
                name="projectAddress"
                value={formData.projectAddress}
                onChange={handleChange}
                placeholder="Where the work is done (site address)"
              />
              <p className="form-hint">
                Used as the project location. Falls back to client address if
                empty.
              </p>
            </div>

            <div className="form-group-project">
              <label htmlFor="deliveryAddress">
                Delivery Address (optional)
              </label>
              <input
                id="deliveryAddress"
                type="text"
                name="deliveryAddress"
                value={formData.deliveryAddress}
                onChange={handleChange}
                placeholder="Where to deliver materials / documents"
              />
            </div>

            <div className="form-group-project">
              <label>Services</label>
              <p className="form-hint">
                Pick each service from the catalog with its tier and task
                groups. Its tasks become the project tasks.
              </p>
              <CatalogServicePicks
                bookingId={formData.bookingId}
                picks={picks}
                onChange={setPicks}
                disabled={submitting}
              />
            </div>

            <div className="form-row-project">
              <div className="form-group-project">
                <label htmlFor="startDate">Start Date</label>
                <input
                  id="startDate"
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group-project">
                <label htmlFor="endDate">End Date</label>
                {formData.deadlineOngoing ? (
                  <input id="endDate" type="text" value="Ongoing" readOnly />
                ) : (
                  <input
                    id="endDate"
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                  />
                )}
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontWeight: 400 }}>
                  <input
                    type="checkbox"
                    checked={Boolean(formData.deadlineOngoing)}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, deadlineOngoing: e.target.checked }))
                    }
                    style={{ width: 'auto' }}
                  />
                  Ongoing (until the client stops)
                </label>
              </div>
            </div>

            <div className="form-group-project">
              <label htmlFor="frequency">Service frequency</label>
              <select
                id="frequency"
                name="frequency"
                value={formData.frequency || 'One-time'}
                onChange={handleChange}
              >
                {PROJECT_FREQUENCY_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              <p className="form-hint">
                Filled from a quotation when this client has one. Change it here
                for this project only. Recurring work stays on this project;
                extra visit-projects are not created.
              </p>
            </div>

            {isRecurringFrequencyOption(formData.frequency) ? (
              <>
                <div className="form-group-project">
                  <label htmlFor="billingCadence">Invoice timing</label>
                  <select
                    id="billingCadence"
                    name="billingCadence"
                    value={formData.billingCadence || 'per_visit'}
                    onChange={handleChange}
                  >
                    <option value="per_visit">Each visit (current default)</option>
                    <option value="monthly">Monthly (end of month, contract)</option>
                  </select>
                  <p className="form-hint">
                    Monthly bills once per month on this project. Set End Date
                    to the contract end (e.g. 1 year).
                  </p>
                </div>
                {formData.billingCadence === 'monthly' ? (
                  <>
                    <div className="form-group-project">
                      <label htmlFor="invoiceLineMode">Monthly invoice lines</label>
                      <select
                        id="invoiceLineMode"
                        name="invoiceLineMode"
                        value={formData.invoiceLineMode || 'service'}
                        onChange={handleChange}
                      >
                        <option value="service">Service package</option>
                        <option value="task">Tasks completed / priced</option>
                      </select>
                    </div>
                    <label
                      className="project-checkbox-row"
                      htmlFor="autoSendMonthlyInvoice"
                    >
                      <input
                        id="autoSendMonthlyInvoice"
                        type="checkbox"
                        name="autoSendMonthlyInvoice"
                        checked={Boolean(formData.autoSendMonthlyInvoice)}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            autoSendMonthlyInvoice: e.target.checked,
                          }))
                        }
                      />
                      <span className="project-checkbox-copy">
                        <strong>Auto-send the monthly invoice</strong>
                        <span>
                          Leave off to create a draft at month end for you to send.
                        </span>
                      </span>
                    </label>
                  </>
                ) : null}
              </>
            ) : null}

            <div className="form-row-project">
              <div className="form-group-project">
                <label htmlFor="startTime">Start Time</label>
                <input
                  id="startTime"
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group-project">
                <label htmlFor="endTime">End Time</label>
                <input
                  id="endTime"
                  type="time"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row-project">
              <div className="form-group-project">
                <label htmlFor="reminderDate">Reminder Date</label>
                <input
                  id="reminderDate"
                  type="date"
                  name="reminderDate"
                  value={formData.reminderDate}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group-project">
                <label htmlFor="reminderTime">Reminder Time</label>
                <input
                  id="reminderTime"
                  type="time"
                  name="reminderTime"
                  value={formData.reminderTime}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row-project">
              <div className="form-group-project">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                >
                  <option value="Draft">Draft</option>
                  <option value="Active">Active</option>
                  <option value="Complete">Complete</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              <div className="form-group-project">
                <label htmlFor="manager">Assign Manager</label>
                <select
                  id="manager"
                  name="manager"
                  value={formData.manager}
                  onChange={handleChange}
                >
                  <option value="">Unassigned</option>
                  {managers.map((m) => {
                    const fullName =
                      `${m.firstName || ''} ${m.lastName || ''}`.trim();
                    return (
                      <option key={m.id} value={m.id}>
                        {fullName || m.email}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            <div className="form-group-project">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter project description"
              />
            </div>
          </div>

          <div className="save-btn-container">
            <button
              type="button"
              className="cancel-btn-project"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button type="submit" className="save-btn" disabled={submitting}>
              {submitting ? 'Creating...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
